import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtHours, fmtDate } from "@/lib/api";
import { LeaveTypeBadge, StatusBadge, BookingDetailDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { toast } from "sonner";
import { CalendarDays, Plus, CalendarRange, ArrowRight, X, Pencil } from "lucide-react";

const yearLabel = (y) => {
  if (!y) return "";
  try {
    const f = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    return `${f(y.start_date)} – ${f(y.end_date)}`;
  } catch { return y.name || ""; }
};
const daysAway = (iso) => {
  try { const n = Math.round((new Date(iso.slice(0, 10)) - new Date(new Date().toDateString())) / 86400000); return n; } catch { return null; }
};

const CalcLine = ({ label, value, bold, accent }) => (
  <div className={`flex justify-between px-3 py-2 rounded-lg ${accent ? "bg-[#EFF8FF]" : "bg-slate-50/60"}`}>
    <span className={bold ? "font-semibold text-[#0B1A2F]" : "text-slate-600"}>{label}</span>
    <span className={`tabular ${accent ? "font-bold text-[#2EA3FD]" : bold ? "font-bold text-[#0B1A2F]" : "text-slate-700"}`}>{value}</span>
  </div>
);

const Tile = ({ label, value, sub, hint, tone = "navy", testid }) => {
  const tones = { navy: "text-[#0B1A2F]", green: "text-emerald-600", blue: "text-[#2EA3FD]", amber: "text-amber-600" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1.5 text-2xl font-extrabold font-display tabular ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      {hint && <p className="text-[11px] text-amber-600 mt-1 font-medium" data-testid={testid ? `${testid}-pending` : undefined}>{hint}</p>}
    </div>
  );
};

// Booking list for a single holiday year (approved + pending, chronological).
function BookingList({ summary, onOpen, onCancel, onEdit, emptyText }) {
  if (!summary) return <p className="text-sm text-slate-400 py-8 text-center">No holiday year configured.</p>;
  const rows = [...(summary.bookings || [])]
    .filter((b) => ["approved", "pending"].includes(b.status))
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
  if (rows.length === 0) return <p className="text-sm text-slate-400 py-8 text-center">{emptyText}</p>;
  return (
    <div className="divide-y divide-slate-100">
      {rows.map((b) => (
        <div key={b.id} className="flex items-center justify-between gap-3 py-2.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
          <button onClick={() => onOpen(b)} data-testid={`module-booking-${b.id}`} className="flex-1 text-left min-w-0">
            <div className="flex items-center gap-2 flex-wrap"><LeaveTypeBadge type={b.leave_type} /><StatusBadge status={b.status} /></div>
            <p className="text-sm text-[#0B1A2F] mt-1">{fmtDate(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtDate(b.end_date)}` : ""}</p>
          </button>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-sm font-semibold tabular text-[#0B1A2F] mr-1">{fmtHours(b.total_hours)}</span>
            {b.status === "pending" && onEdit && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[#2EA3FD] hover:text-[#1B8EE6] hover:bg-sky-50" data-testid={`module-edit-${b.id}`} onClick={() => onEdit(b)}><Pencil size={13} className="mr-1" />Edit</Button>
            )}
            {b.status === "pending" && onCancel && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50" data-testid={`module-cancel-${b.id}`} onClick={() => onCancel(b)}><X size={14} className="mr-1" />Cancel</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LeaveModule({ empId }) {
  const { types } = useLeaveTypes();
  const activeTypes = (types || []).filter((t) => t.active);
  const [cur, setCur] = useState(null);
  const [nxt, setNxt] = useState(null);
  const [nextYear, setNextYear] = useState(null);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("cass-leave-module-tab") || "overview"; } catch { return "overview"; }
  });
  const changeTab = (v) => { setTab(v); try { localStorage.setItem("cass-leave-module-tab", v); } catch { /* noop */ } };
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editType, setEditType] = useState("annual");
  const [editDuration, setEditDuration] = useState("full");
  const [editHours, setEditHours] = useState(0);
  const [editHalfStart, setEditHalfStart] = useState(false);
  const [editPreview, setEditPreview] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (b) => {
    setEditTarget(b); setEditStart(b.start_date); setEditEnd(b.end_date);
    setEditType(b.leave_type || "annual"); setEditDuration(b.duration_type || "full");
    setEditHours(b.hours_value || 0); setEditHalfStart(b.half_day_start || false);
    setEditPreview(null);
  };
  const editSingle = !!editStart && editStart === editEnd;
  const editPayload = () => ({
    start_date: editStart, end_date: editEnd,
    duration_type: editDuration, hours_value: editDuration === "hours" ? Number(editHours) || 0 : 0,
    half_day_start: editDuration === "half" && editSingle ? editHalfStart : false,
    half_day_end: false, leave_type: editType || "annual",
  });

  const load = useCallback(async () => {
    try {
      const [yrsR, curR] = await Promise.all([
        api.get("/holiday-years"),
        api.get(`/employees/${empId}/summary`),
      ]);
      const years = yrsR.data || [];
      const cy = curR.data.holiday_year;
      setCur(curR.data);
      const sorted = [...years].sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
      const ny = sorted.find((y) => cy && y.start_date > cy.end_date) || null;
      setNextYear(ny);
      if (ny) {
        const nR = await api.get(`/employees/${empId}/summary`, { params: { year_id: ny.id } });
        setNxt(nR.data);
      } else setNxt(null);
    } catch { /* noop */ }
    setLoading(false);
  }, [empId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    if (!editTarget || !editStart || !editEnd) { setEditPreview(null); return; }
    api.post("/requests/preview", { employee_id: empId, ...editPayload() })
      .then((r) => setEditPreview(r.data)).catch(() => setEditPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTarget, editStart, editEnd, editType, editDuration, editHours, editHalfStart, empId]);

  const saveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await api.put(`/requests/${editTarget.id}`, editPayload());
      toast.success("Request updated");
      setEditTarget(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.errors && d.errors.join(", ")) || (typeof d === "string" ? d : "Could not update request"));
    }
    setEditSaving(false);
  };

  const doCancel = async () => {
    const b = cancelTarget;
    setCancelTarget(null);
    if (!b) return;
    try {
      await api.post(`/requests/${b.id}/cancel`, { cancellation_reason: "Cancelled by employee" });
      toast.success("Request cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel request");
    }
  };

  if (loading || !cur) {
    return <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 text-slate-400 text-sm" data-testid="leave-module-loading">Loading your leave…</div>;
  }

  const s = cur;
  const total = s.total_entitlement || 0;
  const remaining = s.confirmed_remaining || 0;
  const booked = s.approved_future || 0;
  const taken = s.approved_taken || 0;
  const pending = s.pending || 0;
  const pendingCount = (s.bookings || []).filter((b) => b.status === "pending").length;
  const usedPct = total ? Math.min(100, Math.max(0, Math.round(((taken + booked) / total) * 100))) : 0;
  const nb = s.next_booked;
  let nbLabel = "None booked", nbSub = "Nothing scheduled";
  if (nb) {
    try { nbLabel = new Date(nb.slice(0, 10)).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { nbLabel = nb.slice(0, 10); }
    const away = daysAway(nb);
    nbSub = away == null ? "" : away > 0 ? `in ${away} day${away === 1 ? "" : "s"}` : away === 0 ? "today" : "in progress";
  }
  const ent = s.entitlement || {};

  const today = new Date().toISOString().slice(0, 10);
  const pendingFuture = (s.bookings || []).filter((b) => b.status === "pending" && b.start_date >= today).sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
  let pendingHint = "";
  if (pendingFuture.length) {
    let pl;
    try { pl = new Date(pendingFuture[0].start_date.slice(0, 10)).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
    catch { pl = pendingFuture[0].start_date.slice(0, 10); }
    pendingHint = `${pendingFuture.length} pending · next ${pl}`;
  }
  const calDate = (nb && nb.slice(0, 10)) || (pendingFuture[0] && pendingFuture[0].start_date.slice(0, 10)) || null;
  const calendarHref = calDate ? `/calendar?date=${calDate}` : "/calendar";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="leave-module">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pt-5 flex-wrap">
        <Link to="/requests" data-testid="leave-module-title" className="group inline-flex items-center gap-2 text-[#0B1A2F] hover:text-[#2EA3FD] transition-colors">
          <span className="rounded-lg bg-[#EFF8FF] p-2 text-[#2EA3FD]"><CalendarDays size={18} /></span>
          <span>
            <span className="block text-lg font-bold font-display leading-tight">Leave &amp; Absence</span>
            <span className="block text-xs text-slate-400">{yearLabel(s.holiday_year)}</span>
          </span>
          <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
        </Link>
        <Button asChild className="bg-[#2EA3FD] hover:bg-[#1B8EE6] h-10"><Link to="/requests" data-testid="leave-module-book"><Plus size={16} className="mr-1.5" />Book leave</Link></Button>
      </div>

      {/* Headline KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-5 sm:px-6 pt-4">
        <Tile testid="module-kpi-remaining" label="Confirmed remaining" value={fmtHours(remaining)} tone="green" sub={`${fmtHours(s.remaining_if_pending)} if pending approved`} />
        <Tile testid="module-kpi-entitlement" label="Total entitlement" value={fmtHours(total)} sub={`Core ${fmtHours(s.core_entitlement)}`} />
        <Tile testid="module-kpi-taken" label="Taken / booked" value={fmtHours(taken)} tone="navy" sub={`${fmtHours(booked)} booked · ${fmtHours(pending)} pending`} />
        <Tile testid="module-kpi-next" label="Next booked leave" value={nbLabel} tone="blue" sub={nbSub} hint={pendingHint} />
      </div>

      {/* Sub-tabs */}
      <Tabs value={tab} onValueChange={changeTab} className="px-5 sm:px-6 py-5 mt-1">
        <TabsList className="bg-slate-100 flex-wrap h-auto" data-testid="leave-module-tabs">
          <TabsTrigger value="overview" data-testid="module-tab-overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="booked" data-testid="module-tab-booked" className="data-[state=active]:bg-white">Booked</TabsTrigger>
          <TabsTrigger value="taken" data-testid="module-tab-taken" className="data-[state=active]:bg-white">Taken</TabsTrigger>
          <TabsTrigger value="entitlement" data-testid="module-tab-entitlement" className="data-[state=active]:bg-white">Entitlement</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="pt-4 space-y-4" data-testid="module-panel-overview">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile label="Total entitlement" value={fmtHours(total)} />
            <Tile label="Taken" value={fmtHours(taken)} />
            <Tile label="Booked" value={fmtHours(booked)} tone="blue" />
            <Tile label="Confirmed remaining" value={fmtHours(remaining)} tone="green" />
            <Tile label="Pending requests" value={pendingCount} sub={pending ? `${fmtHours(pending)} awaiting` : "None awaiting"} tone="amber" />
            <Tile label="Next booked leave" value={nbLabel} sub={nbSub} hint={pendingHint} tone="blue" testid="overview-tile-next" />
          </div>
          <div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#2EA3FD] transition-all" style={{ width: `${usedPct}%` }} data-testid="module-progress-bar" /></div>
            <p className="text-xs text-slate-400 mt-2">{usedPct}% of your entitlement is booked or taken.</p>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild className="bg-[#2EA3FD] hover:bg-[#1B8EE6]"><Link to="/requests" data-testid="module-overview-book"><Plus size={15} className="mr-1.5" />Book leave</Link></Button>
            <Button asChild variant="outline"><Link to={calendarHref} data-testid="module-view-calendar"><CalendarRange size={15} className="mr-1.5" />View leave calendar</Link></Button>
            <Button asChild variant="outline"><Link to={`/employees/${empId}`} data-testid="module-view-breakdown">View full breakdown<ArrowRight size={15} className="ml-1.5" /></Link></Button>
          </div>
        </TabsContent>

        {/* Booked — current + next holiday year side by side */}
        <TabsContent value="booked" className="pt-4" data-testid="module-panel-booked">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-xl p-4" data-testid="booked-current-year">
              <div className="flex items-baseline justify-between mb-1">
                <h4 className="font-semibold text-[#0B1A2F]">Current holiday year</h4>
                <span className="text-xs text-slate-400">{yearLabel(cur.holiday_year)}</span>
              </div>
              <BookingList summary={cur} onOpen={setDetail} onCancel={setCancelTarget} onEdit={openEdit} emptyText="No bookings in the current holiday year yet." />
            </div>
            <div className="border border-slate-200 rounded-xl p-4" data-testid="booked-next-year">
              <div className="flex items-baseline justify-between mb-1">
                <h4 className="font-semibold text-[#0B1A2F]">Next holiday year</h4>
                <span className="text-xs text-slate-400">{nextYear ? yearLabel(nextYear) : "Not configured"}</span>
              </div>
              {nextYear ? <BookingList summary={nxt} onOpen={setDetail} onCancel={setCancelTarget} onEdit={openEdit} emptyText="No bookings in the next holiday year yet." />
                : <p className="text-sm text-slate-400 py-8 text-center">The next holiday year has not been set up yet.</p>}
            </div>
          </div>
        </TabsContent>

        {/* Taken */}
        <TabsContent value="taken" className="pt-4 space-y-4" data-testid="module-panel-taken">
          {(s.leave_type_breakdown || []).filter((lt) => lt.approved_hours > 0).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {s.leave_type_breakdown.filter((lt) => lt.approved_hours > 0).map((lt) => (
                <div key={lt.leave_type} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`taken-tile-${lt.leave_type}`}>
                  <p className="text-xs uppercase tracking-wide text-slate-500 truncate">{lt.name}</p>
                  <p className="text-2xl font-bold text-[#0B1A2F] tabular mt-1">{fmtHours(lt.approved_hours)}</p>
                  <p className="text-xs text-slate-400 mt-1">{lt.count} booking{lt.count !== 1 ? "s" : ""}{lt.approved_days ? ` · ${lt.approved_days}d` : ""}</p>
                </div>
              ))}
            </div>
          )}
          <div className="border border-slate-200 rounded-xl overflow-x-auto cass-scroll">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{["Type", "Dates", "Hours", "Days", "Status"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {(s.bookings || []).filter((b) => b.status === "approved" && b.end_date < new Date().toISOString().slice(0, 10)).sort((a, b) => (a.start_date < b.start_date ? 1 : -1)).map((b) => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(b)} data-testid={`taken-row-${b.id}`}>
                    <TableCell><LeaveTypeBadge type={b.leave_type} /></TableCell>
                    <TableCell className="text-sm">{fmtDate(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtDate(b.end_date)}` : ""}</TableCell>
                    <TableCell className="tabular">{fmtHours(b.total_hours)}</TableCell>
                    <TableCell className="tabular">{b.total_days}</TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                  </TableRow>
                ))}
                {(s.bookings || []).filter((b) => b.status === "approved" && b.end_date < new Date().toISOString().slice(0, 10)).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No leave taken yet this year.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Entitlement */}
        <TabsContent value="entitlement" className="pt-4 space-y-4" data-testid="module-panel-entitlement">
          <div className="border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-[#0B1A2F]">Entitlement breakdown</h4>
              {ent.proration_method && <span className="text-xs text-slate-500">Proration: {ent.proration_method} · Rounding: {ent.rounding_method}</span>}
            </div>
            {(ent.periods || []).length > 0 && (
              <div className="overflow-x-auto cass-scroll mb-4">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">{["Period", "Pattern", "Avg wk hrs", "Allowance", "Full-year", "Proration", "Prorated"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {ent.periods.map((p, i) => (
                      <TableRow key={i} data-testid="module-entitlement-row">
                        <TableCell className="text-sm">{fmtDate(p.start_date)} – {fmtDate(p.end_date)}</TableCell>
                        <TableCell>{p.pattern_name || <span className="text-amber-600">No pattern</span>}{p.cycle_length === 2 ? " (2-wk)" : ""}</TableCell>
                        <TableCell className="tabular">{p.avg_weekly_hours}h</TableCell>
                        <TableCell className="tabular">{p.allowance_weeks} wks</TableCell>
                        <TableCell className="tabular">{fmtHours(p.full_year_entitlement)}</TableCell>
                        <TableCell className="text-xs text-slate-500">{p.proration_factor_desc}</TableCell>
                        <TableCell className="tabular font-semibold text-[#0B1A2F]">{fmtHours(p.prorated_entitlement)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <CalcLine label="Core entitlement (rounded)" value={fmtHours(s.core_entitlement)} bold />
              <CalcLine label="Carry-forward" value={fmtHours(s.carry_forward)} />
              <CalcLine label="Adjustments" value={fmtHours(s.adjustments_total)} />
              <CalcLine label="Total entitlement" value={fmtHours(total)} bold />
              <CalcLine label="Approved taken" value={`− ${fmtHours(taken)}`} />
              <CalcLine label="Approved future" value={`− ${fmtHours(booked)}`} />
              <CalcLine label="Confirmed remaining" value={fmtHours(remaining)} bold accent />
              <CalcLine label="Pending (not deducted)" value={fmtHours(pending)} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <BookingDetailDialog booking={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="module-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this request?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget && <>Your pending request for {fmtDate(cancelTarget.start_date)}{cancelTarget.start_date !== cancelTarget.end_date ? ` – ${fmtDate(cancelTarget.end_date)}` : ""} ({fmtHours(cancelTarget.total_hours)}) will be withdrawn. This can't be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="module-cancel-keep">Keep request</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} className="bg-amber-600 hover:bg-amber-700" data-testid="module-cancel-confirm">Yes, cancel it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-md" data-testid="module-edit-dialog">
          <DialogHeader>
            <DialogTitle>Adjust your dates</DialogTitle>
            <DialogDescription>{editTarget ? `Pending request${editTarget.reference ? ` · ${editTarget.reference}` : ""}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">First date</Label><Input type="date" value={editStart} data-testid="module-edit-start" onChange={(e) => { const v = e.target.value; setEditStart(v); setEditEnd((prev) => (prev && prev >= v ? prev : v)); }} /></div>
            <div><Label className="text-xs text-slate-500">Last date</Label><Input type="date" value={editEnd} min={editStart} data-testid="module-edit-end" onChange={(e) => setEditEnd(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Leave type</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger data-testid="module-edit-leavetype"><SelectValue placeholder="Leave type" /></SelectTrigger>
                <SelectContent>{activeTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Duration</Label>
              <Select value={editDuration} onValueChange={setEditDuration}>
                <SelectTrigger data-testid="module-edit-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full day(s)</SelectItem>
                  <SelectItem value="half">Half day</SelectItem>
                  <SelectItem value="hours">Specific hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {editDuration === "hours" && (
            <div><Label className="text-xs text-slate-500">Hours per day</Label><Input type="number" step="0.5" min="0" value={editHours} data-testid="module-edit-hours" onChange={(e) => setEditHours(e.target.value)} /></div>
          )}
          {editDuration === "half" && editSingle && (
            <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={editHalfStart} onCheckedChange={(v) => setEditHalfStart(!!v)} data-testid="module-edit-half-start" />Half day (AM / start)</label>
          )}
          {editDuration === "half" && !editSingle && (
            <p className="text-xs text-amber-600">Half day applies to single-date requests. Set the same first and last date to use it.</p>
          )}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid="module-edit-preview">
            {editPreview
              ? (editPreview.errors && editPreview.errors.length
                ? <span className="text-rose-600">{editPreview.errors.join(", ")}</span>
                : <span className="text-slate-700">New total: <span className="font-semibold text-[#0B1A2F]">{fmtHours(editPreview.total_hours)}</span>{editDuration !== "hours" ? <> over {Math.round(editPreview.total_days * 100) / 100} day{editPreview.total_days === 1 ? "" : "s"}</> : null}</span>)
              : <span className="text-slate-400">Choose dates to preview the new total…</span>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="module-edit-cancel-btn">Cancel</Button>
            <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" disabled={editSaving || !editStart || !editEnd || (editPreview?.errors?.length > 0)} onClick={saveEdit} data-testid="module-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
